import { useCallback, useEffect, useRef, useState } from "react";
import { Button, ButtonGroup } from "reactstrap";
import {
  SELECTION_CHANGE_COMMAND,
  FORMAT_TEXT_COMMAND,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  TextFormatType,
} from "lexical";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUnderline, faBold, faItalic, faStrikethrough } from "@fortawesome/free-solid-svg-icons";

import { toLexical } from "./../Serialize";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import { $patchStyleText, $getSelectionStyleValueForProperty } from "@lexical/selection";
import TextEditorDropDown from "../TextEditorDropDown";
import { RichText } from "../../../model/viewModel/richText";
import { getDefaultStyle } from "./../../../utils/richTextFunctions";

const LowPriority = 1;

interface IToolbarPlugin {
  richTexts: RichText[];
}

export default function ToolbarPlugin({ richTexts }: IToolbarPlugin) {
  const { fontSize: defaultFontSize, color: defaultColor } = getDefaultStyle() as {
    fontSize: string;
    color: string;
  };
  const [editor] = useLexicalComposerContext();
  const [activeEditor] = useState(editor);
  const [fontSize, setFontSize] = useState<string>(defaultFontSize);
  const [fontColor, setFontColor] = useState<string>(defaultColor);
  const toolbarRef = useRef(null);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);

  useEffect(() => {
    editor.update(() => {
      const root = $getRoot();
      toLexical(root, richTexts);
      const nodes = root.getAllTextNodes();
      if (nodes.length) {
        nodes[0].selectPrevious();
      } else {
        root.getFirstChild()?.selectStart();
      }
    });
  }, []);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow();
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));
      const fontSize =
        $getSelectionStyleValueForProperty(selection, "font-size", defaultFontSize) ||
        defaultFontSize;
      setFontSize(fontSize);
      const color =
        $getSelectionStyleValueForProperty(selection, "color", defaultColor) || defaultColor;
      setFontColor(color);
    }
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          updateToolbar();
          return false;
        },
        LowPriority,
      ),
    );
  }, [editor, updateToolbar]);

  const applyStyleText = useCallback(
    (styles: Record<string, string>) => {
      activeEditor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, styles);
        }
      });
    },
    [activeEditor],
  );

  const onFontColorSelect = useCallback(
    (value: string) => {
      applyStyleText({ color: value });
    },
    [applyStyleText],
  );

  const onFontSizeSelect = useCallback(
    (option: string) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, {
            "font-size": option,
          });
        }
      });
    },
    [editor],
  );

  const FONT_SIZE_OPTIONS: [string, string][] = [
    ["12px", "12px"],
    ["14px", "14px"],
    ["16px", "16px"],
    ["18px", "18px"],
    ["20px", "20px"],
    ["22px", "22px"],
    ["24px", "24px"],
  ];

  const FONT_COLOR_OPTIONS: [string, string][] = [
    ["black", "Black"],
    ["red", "Red"],
    ["green", "Green"],
    ["blue", "Blue"],
  ];

  const TEXT_FORMATS: [TextFormatType, boolean, IconProp][] = [
    ["bold", isBold, faBold],
    ["italic", isItalic, faItalic],
    ["underline", isUnderline, faUnderline],
    ["strikethrough", isStrikethrough, faStrikethrough],
  ];

  return (
    <div ref={toolbarRef}>
      <ButtonGroup size="sm" className="mr-1">
        {TEXT_FORMATS.map(([format, func, icon]) => (
          <Button
            key={format}
            color="dark"
            className={func ? "active m-1" : "m-1"}
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
            }}
          >
            <FontAwesomeIcon icon={icon} />
          </Button>
        ))}
      </ButtonGroup>
      <ButtonGroup size="sm">
        <TextEditorDropDown
          value={fontSize}
          icon={false}
          array={FONT_SIZE_OPTIONS}
          onClick={(e) => onFontSizeSelect(e)}
        />
        <TextEditorDropDown
          value={fontColor}
          icon={true}
          array={FONT_COLOR_OPTIONS}
          onClick={(e) => onFontColorSelect(e)}
        />
      </ButtonGroup>
    </div>
  );
}
